import { Alert, AlertDescription, AlertTitle } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';

import { RefreshButton } from '@/design';
import { useCan } from '@/domains/auth';
import type { IEtagged } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type { IExportCycles, IRankingEdition, IRankingOverview } from '../../api/ranking-process-contracts';
import { rankingProcessApi } from '../../api/ranking-process-api';
import type { IRankingList } from '../../api/ranking-contracts';
import { rankingsApi } from '../../api/rankings-api';
import { AdminPage, LoadError, useAdminData, type IAdminTab } from '../../components/admin-page';
import { useListReread } from '../../components/list-area';
import { rankingPath } from '../../paths';
import { countText, exportedNotImported, madeNotExported } from '../../rankings';
import { refusalMessage } from '../../refusal';
import { RankingStatusChip } from './editions';
import { RecordActions } from './publish';

/**
 * One ranking's record (SR-231), `/team/rankings/:survey/:list/:year`, the editions beside it: a
 * Lightning record page — the name, a highlights strip (status, year, placements, territories,
 * list, cycle, and the moment its figures are frozen at, `[data-frozen-at]` — SR-451), the record
 * writes (`publish.tsx`), and routed tabs **in the order of the process**
 * (Дмитрий's review of 19 Oct: a reader new to it found Import before the export it follows):
 * Export for scoring, Import scores, Placements (the root), Finalists, History — then List setup,
 * the list's template and territories, which every year shares. **A "Next step" strip** under the
 * highlights says where the edition stands — export and import, review and the finalists' review by
 * their firms (Veritant's timeline: before the results go to Forbes), publish — from its status, and
 * from whether a draft holds anything yet: a draft the export made and no import filled (SR-451)
 * stands at the import, the export done. Each step a link to its tab.
 *
 * **The record holds the one read of the ranking and its `ETag`**, and the tabs read it through
 * `useRanking`. An edit's answer hands the record the `ETag` it left (`adopt`), and a re-read's
 * replaces it only when its `version` is not older — a read begun before an edit and landing after
 * it would otherwise put back an `ETag` the edit spent. The `ETag` itself is never parsed.
 *
 * **No ranking at the address is a state, not a failure**: the first export makes the draft (SR-451;
 * an import with no export before it does too), so the record draws the list's name and year with
 * "Not exported yet", and Export, Import and List setup alone to `rankings.manage`. The export
 * reads the record again once its file is written, so the draft it made is drawn at once. The
 * Import tab is hidden from anyone else (a section, SR-228's rule); the edit controls inside
 * Placements are drawn disabled for them instead.
 */

export interface IRankingRecord {
  key: { surveyCode: string; list: string; year: string };
  /** Null while the first read is out or failed, and where the address holds no ranking. */
  overview: Nullable<IRankingOverview>;
  /** Whether the address holds no ranking (a 404). */
  missing: boolean;
  /** The `ETag` an edit quotes — null where none was read. */
  etag: Nullable<string>;
  /** The caller holds `rankings.manage`. */
  permitted: boolean;
  /** The list's label from the catalogue, null until read. */
  listLabel: Nullable<string>;
  /** Whether the list ranks in territories — the ranking's word, else the catalogue's. */
  territorial: boolean;
  /** A draft the caller may edit. */
  editable: boolean;
  /** Takes an edit's `ETag` and version. */
  adopt: (etag: Nullable<string>, version: number) => void;
  /** Reads the ranking (and the editions beside it) again — after a write, whose answer the tab
   *  has already drawn. */
  reread: () => Promise<boolean>;
  /** Refresh: the ranking, the editions and what the open tab drew from its own reads. */
  readAgain: () => Promise<boolean>;
  /** How often Refresh was pressed — what a tab's own read re-runs on. */
  freshness: number;
  /** How often a Refresh landed — what clears a tab's last refusal. */
  cleared: number;
  /** Takes a record write's answer — the one-ranking read and its `ETag` — and with `rows` has
   *  the open tab read its rows again (a publish may have closed gaps). */
  adoptRead: (answer: IEtagged<IRankingOverview>, rows: boolean) => void;
  /** The cycles pinning the survey and the one the ranking is linked to (SR-415) — read once
   *  for the record, so the Export and Import tabs and "Cycle…" share it; null while out or
   *  failed. */
  exportCycles: Nullable<IExportCycles>;
  exportCyclesError: Nullable<string>;
  /** The cycle chosen on the Export or Import tab (SR-415): one choice for both tabs, kept for
   *  the visit (`cycle-choice.tsx`); null for the ranking's link, else the newest dated cycle. */
  chosenCycle: Nullable<string>;
  chooseCycle: (code: Nullable<string>) => void;
  /** How many figures moved since the ranking's snapshot (SR-451), whoever moved them — for the
   *  highlights and the Export tab; null with no snapshot, while out or failed. */
  snapshotChanges: Nullable<number>;
  /** The snapshot dialog, opened from the record's actions, the highlights or the Export tab. */
  snapshotOpen: boolean;
  setSnapshotOpen: (open: boolean) => void;
}

const RankingContext = React.createContext<Nullable<IRankingRecord>>(null);

export function useRanking(): IRankingRecord {
  const record = React.useContext(RankingContext);
  if (record === null) throw new Error('useRanking outside a ranking record');
  return record;
}

export function AdminRankingRecord() {
  const { survey = '', list = '', year = '' } = useParams();
  // Keyed by the address, so nothing of one ranking is drawn under another's.
  return <RankingRecord key={`${survey}/${list}/${year}`} surveyCode={survey} list={list} year={year} />;
}

interface IHeld {
  etag: Nullable<string>;
  version: number;
}

function RankingRecord({ surveyCode, list, year }: { surveyCode: string; list: string; year: string }) {
  const key = React.useMemo(() => ({ surveyCode, list, year }), [surveyCode, list, year]);
  const permitted = useCan()('rankings.manage');
  const rereadList = useListReread();
  const [held, setHeld] = React.useState<IHeld>({ etag: null, version: -1 });
  const keep = React.useCallback((etag: Nullable<string>, version: number) => {
    setHeld((current) => (version >= current.version ? { etag, version } : current));
  }, []);

  const { data, error, reload, adopt: adoptOverview } = useAdminData<{ read: Nullable<IEtagged<IRankingOverview>> }>(
    async (signal) => {
      try {
        const read = await rankingProcessApi.overview(key, signal);
        if (read) keep(read.etag, read.body.ranking.version);
        return { read };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read the ranking'));
      }
    }
  );
  const { data: listed } = useAdminData<Nullable<IRankingList>>(async () => {
    const catalogue = await rankingsApi.lists(surveyCode).catch(() => null);
    return catalogue?.lists.find((entry) => entry.list === list) ?? null;
  });
  const label = listed?.label ?? null;

  const reread = React.useCallback(async () => {
    rereadList();
    return reload();
  }, [reload, rereadList]);
  const [freshness, setFreshness] = React.useState(0);
  const [cleared, setCleared] = React.useState(0);
  const [chosenCycle, chooseCycle] = React.useState<Nullable<string>>(null);
  // Lists' Refresh: held off while its read is out, and "updated 10:44" once it lands.
  const [refreshing, setRefreshing] = React.useState(false);
  const [readAt, setReadAt] = React.useState<Nullable<Date>>(null);
  React.useEffect(() => {
    if (data !== null) setReadAt(new Date());
  }, [data]);
  const readAgain = React.useCallback(async () => {
    setFreshness((count) => count + 1);
    setRefreshing(true);
    try {
      const read = await reread();
      if (read) setCleared((count) => count + 1);
      return read;
    } finally {
      setRefreshing(false);
    }
  }, [reread]);

  const adoptRead = React.useCallback(
    (answer: IEtagged<IRankingOverview>, rows: boolean) => {
      adoptOverview({ read: answer });
      keep(answer.etag, answer.body.ranking.version);
      rereadList();
      if (rows) setFreshness((count) => count + 1);
    },
    [adoptOverview, keep, rereadList]
  );

  const overview = data?.read?.body ?? null;
  const missing = data !== null && data.read === null;
  // The cycles (SR-415), read once the ranking's read has settled — its link is the choice's
  // default — and again when the link moves and on Refresh.
  const settled = data !== null;
  const linkedCycle = overview?.ranking.cycleCode ?? null;
  const { data: exportCycles, error: exportCyclesError } = useAdminData<Nullable<IExportCycles>>(
    (signal) => (settled ? rankingProcessApi.exportCycles(key, signal) : Promise.resolve(null)),
    [settled, linkedCycle, freshness]
  );
  // A link that moved (an import, "Cycle…") is the choice again: a choice made before it is spent.
  const [choiceLink, setChoiceLink] = React.useState(linkedCycle);
  if (choiceLink !== linkedCycle) {
    setChoiceLink(linkedCycle);
    chooseCycle(null);
  }
  const territorial = overview?.ranking.territorial ?? listed?.territorial ?? false;
  // What moved since the snapshot (SR-451): read again when the snapshot moves and on Refresh.
  const snapshotAt = overview?.ranking.frozenAt ?? null;
  const { data: snapshotChanges } = useAdminData<Nullable<number>>(
    (signal) =>
      snapshotAt
        ? rankingProcessApi.freezeChanges(key, null, signal).then((read) => read.changes.total)
        : Promise.resolve(null),
    [snapshotAt, freshness]
  );
  const [snapshotOpen, setSnapshotOpen] = React.useState(false);
  const record = React.useMemo<IRankingRecord>(
    () => ({
      key,
      overview,
      missing,
      etag: held.etag,
      permitted,
      listLabel: label,
      territorial,
      editable: permitted && overview?.ranking.status === 'draft',
      adopt: keep,
      reread,
      readAgain,
      freshness,
      cleared,
      adoptRead,
      exportCycles,
      exportCyclesError,
      chosenCycle,
      chooseCycle,
      snapshotChanges: snapshotAt ? snapshotChanges : null,
      snapshotOpen,
      setSnapshotOpen
    }),
    [
      key,
      overview,
      missing,
      held.etag,
      permitted,
      label,
      territorial,
      keep,
      reread,
      readAgain,
      freshness,
      cleared,
      adoptRead,
      exportCycles,
      exportCyclesError,
      chosenCycle,
      snapshotAt,
      snapshotChanges,
      snapshotOpen
    ]
  );

  const title = overview?.ranking.name ?? `${year} ${label ?? list}`;
  const tabs: IAdminTab[] = [
    { to: rankingPath(key, 'export'), label: 'Export for scoring' },
    ...(permitted ? [{ to: rankingPath(key, 'import'), label: 'Import scores' }] : []),
    { to: rankingPath(key), label: 'Placements', end: true },
    ...(permitted ? [{ to: rankingPath(key, 'finalists'), label: 'Finalists' }] : []),
    { to: rankingPath(key, 'history'), label: 'History' },
    { to: rankingPath(key, 'threshold'), label: 'Metrics threshold' },
    { to: rankingPath(key, 'setup'), label: 'List setup' }
  ];
  // With no ranking yet, the steps that need none.
  const before = ['Export for scoring', 'Import scores', 'List setup'];

  return (
    <RankingContext.Provider value={record}>
      <AdminPage
        title={title}
        crumb={`Rankings · ${surveyCode}`}
        tabs={missing ? tabs.filter((tab) => before.includes(tab.label)) : tabs}
        meta={
          <div className="grid gap-2">
            <Highlights record={record} />
            {(overview || (missing && permitted)) && <NextStep record={record} />}
          </div>
        }
        actions={
          <>
            <span className="inline-flex items-center gap-1">
              <RefreshButton
                reading={refreshing || data === null}
                readAt={readAt}
                onClick={() => void readAgain()}
              />
            </span>
            <RecordActions />
          </>
        }
      >
        {error && data !== null && (
          <LoadError message={`Could not refresh — showing the ranking as last read. ${error}`} />
        )}
        {data === null ? (
          error ? (
            <LoadError message={error} />
          ) : (
            <p className="text-sm text-muted-foreground">Reading the ranking…</p>
          )
        ) : missing && !permitted ? (
          <Alert>
            <AlertTitle>No ranking here</AlertTitle>
            <AlertDescription>
              No {surveyCode} ranking is kept for this list and year yet.
            </AlertDescription>
          </Alert>
        ) : (
          <Outlet />
        )}
      </AdminPage>
    </RankingContext.Provider>
  );
}

/** "· 26 changes since": a press that opens the snapshot dialog on a draft, words elsewhere. */
function SnapshotChanges({ count, onOpen }: { count: number; onOpen: Nullable<() => void> }) {
  const words = count === 0 ? 'no change since' : `${countText(count)} ${count === 1 ? 'change' : 'changes'} since`;
  return (
    <span className="ml-1.5 font-normal" data-snapshot-changes={count}>
      ·{' '}
      {onOpen && count > 0 ? (
        <button type="button" className="text-primary underline" onClick={onOpen}>
          {words}
        </button>
      ) : (
        <span className="text-muted-foreground">{words}</span>
      )}
    </span>
  );
}

/** The highlights strip — the record's key facts, Salesforce's highlights panel. */
function Highlights({ record }: { record: IRankingRecord }) {
  const ranking = record.overview?.ranking;
  if (!ranking) {
    return record.missing ? <span data-ranking-status="none">Not exported yet</span> : null;
  }
  const facts: [string, string, string][] = [
    ['year', 'Year', String(ranking.year)],
    ['placements', 'Placements', countText(ranking.placements)],
    [
      'territories',
      'Territories',
      ranking.territorial ? countText(ranking.territories) : 'National'
    ],
    ['list', 'List', ranking.listLabel],
    ['cycle', 'Cycle', ranking.cycleCode ?? '—']
  ];
  // The snapshot of the metrics archive the ranking reads (SR-451; `frozenAt` on the wire). A
  // draft without one says the next export takes it; a published ranking without one —
  // Salesforce's, a staff placement's — says nothing, since nothing will. Beside it, what moved
  // since, which opens the snapshot dialog on a draft.
  const frozenAt = ranking.frozenAt ?? null;
  const changes = record.snapshotChanges;
  const frozen =
    frozenAt !== null ? (
      <div
        className="flex items-baseline gap-1.5"
        data-highlight="frozen"
        data-frozen-at={frozenAt}
      >
        <dt className="text-xs text-muted-foreground">Snapshot</dt>
        <dd className="font-medium tabular-nums">
          {formatDateTime(frozenAt)}
          {changes !== null && (
            <SnapshotChanges
              count={changes}
              onOpen={ranking.status === 'draft' ? () => record.setSnapshotOpen(true) : null}
            />
          )}
        </dd>
      </div>
    ) : ranking.status === 'draft' ? (
      <div className="flex items-baseline gap-1.5" data-highlight="frozen">
        <dt className="text-xs text-muted-foreground">Snapshot</dt>
        <dd className="font-medium">Not yet — the first export takes it</dd>
      </div>
    ) : null;
  return (
    <FactStrip
      label="Highlights"
      hook="data-highlight"
      facts={facts}
      trailing={
        <>
          {frozen}
          <ThresholdFact ranking={ranking} />
        </>
      }
    >
      <div className="flex items-center gap-1.5" data-highlight="status">
        <dt className="sr-only">Status</dt>
        <dd>
          <RankingStatusChip status={ranking.status} />
        </dd>
      </div>
    </FactStrip>
  );
}

/** Whether the edition holds the threshold Metrics updates reads it by (SR-315), and the way
 *  there — where it is said in words and changed. Absent from an older API: nothing drawn. */
function ThresholdFact({ ranking }: { ranking: IRankingEdition }) {
  if (ranking.metricThreshold === undefined) return null;
  return (
    <div className="flex items-baseline gap-1.5" data-highlight="threshold">
      <dt className="text-xs text-muted-foreground">Metrics threshold</dt>
      <dd className="font-medium">
        <Link className="underline-offset-2 hover:underline" to={rankingPath(ranking, 'threshold')}>
          {ranking.metricThreshold ? 'Set' : 'Not set'}
        </Link>
      </dd>
    </div>
  );
}

/** The process's steps, in order, and what each needs from the edition to count as done. */
const STEPS = [
  { key: 'export', label: 'Export for scoring', tab: 'export' },
  { key: 'import', label: 'Import scores', tab: 'import' },
  { key: 'review', label: 'Review and edit', tab: 'placements' },
  // Veritant's timeline: once the ranking is built, finalists go back to their firms for review
  // (edits or removals) before the results go to Forbes — so before publishing, on the draft.
  { key: 'finalists', label: 'Finalists to firms', tab: 'finalists' },
  { key: 'publish', label: 'Publish', tab: 'placements' }
] as const;

/**
 * Where the edition stands in the process: none yet — export, score in Excel and import; a draft
 * the export made and nothing filled (SR-451) — the export done, score and import; a draft holding
 * placements — review and edit, and the finalists' review by their firms, then publish; published
 * — every step done, *Back to draft* to change it. Nothing records a firm's file, so that step is
 * "done" by the status that follows it. Each step links to its tab; the current one is marked
 * (`aria-current`).
 */
function NextStep({ record }: { record: IRankingRecord }) {
  const ranking = record.overview?.ranking ?? null;
  const status = ranking?.status ?? null;
  const exported = ranking !== null && exportedNotImported(ranking);
  const fresh = ranking !== null && madeNotExported(ranking);
  // A published ranking has done every step; a status this build does not know marks none.
  const current =
    status === null || fresh
      ? 'export'
      : exported
        ? 'import'
        : status === 'draft'
          ? 'review'
          : status === 'published'
            ? 'done'
            : null;
  const at = current === 'done' ? STEPS.length : STEPS.findIndex((step) => step.key === current);
  const hint = !record.permitted
    ? fresh
      ? 'Made, not exported yet.'
      : exported
      ? 'Exported, not imported yet: being scored.'
      : status === 'draft'
        ? 'Draft: being reviewed and edited.'
        : status === 'published'
          ? 'Published.'
          : null
    : status === null || fresh
      ? 'Next: export the file, score it in Excel, then import it.'
      : exported
        ? 'Exported. Next: score the file in Excel, then import it.'
        : status === 'draft'
        ? 'Next: review and edit the draft, send the finalists’ files to their firms and apply ' +
          'their answers, then press Publish…'
        : status === 'published'
          ? 'Published. To change it, press Back to draft…'
          : null;
  // A step links only to a tab the record draws: Import scores and Finalists are rankings.manage's,
  // and with no ranking yet nothing past the import is there to open.
  const open = (step: (typeof STEPS)[number]) =>
    (record.permitted || (step.key !== 'import' && step.key !== 'finalists')) &&
    (status !== null || step.key === 'export' || step.key === 'import');
  return (
    <nav aria-label="Ranking steps" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <ol className="flex flex-wrap items-center gap-1">
        {STEPS.map((step, index) => {
          const done = at >= 0 && index < at;
          // With no ranking, export and import are one step of the reader's; on a draft, review
          // and the firms' answers go hand in hand: both current.
          const isCurrent =
            index === at ||
            ((status === null || fresh) && step.key === 'import') ||
            (status === 'draft' && !exported && !fresh && step.key === 'finalists');
          const look = isCurrent
            ? 'rounded-full bg-[var(--color-royal-blue)] px-2 py-0.5 text-white'
            : done || !open(step)
              ? 'rounded-full px-2 py-0.5 text-muted-foreground'
              : 'rounded-full px-2 py-0.5 text-[var(--color-royal-blue)] hover:underline';
          const face = (
            <>
              {done && <span aria-hidden>✓ </span>}
              {step.label}
            </>
          );
          return (
            <li key={step.key} className="flex items-center gap-1" data-step={step.key}>
              {index > 0 && (
                <span aria-hidden className="text-muted-foreground">
                  →
                </span>
              )}
              {open(step) ? (
                <Link
                  to={rankingPath(record.key, step.tab)}
                  aria-current={isCurrent ? 'step' : undefined}
                  data-done={done ? '' : undefined}
                  className={look}
                >
                  {face}
                </Link>
              ) : (
                <span
                  aria-current={isCurrent ? 'step' : undefined}
                  data-done={done ? '' : undefined}
                  className={look}
                >
                  {face}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {hint && (
        <span className="text-muted-foreground" data-next-step>
          {hint}
        </span>
      )}
    </nav>
  );
}

/** A row of label-and-value facts, each wrapper carrying `hook` — the highlights strip and the
 *  import report's counts. `children` are facts of the caller's drawn first, `trailing` last. */
export function FactStrip({
  label,
  hook,
  facts,
  children,
  trailing
}: {
  label?: string;
  hook: `data-${string}`;
  facts: readonly (readonly [key: string, name: string, value: string])[];
  children?: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <dl aria-label={label} className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
      {children}
      {facts.map(([key, name, value]) => (
        <div key={key} className="flex items-baseline gap-1.5" {...{ [hook]: key }}>
          <dt className="text-xs text-muted-foreground">{name}</dt>
          <dd className="font-medium tabular-nums">{value}</dd>
        </div>
      ))}
      {trailing}
    </dl>
  );
}
